export const leadInterests = ["loyalty", "platform", "pilot"] as const;
export const leadLocales = ["en", "zh-Hant", "de"] as const;

export type LeadInterest = (typeof leadInterests)[number];
export type LeadLocale = (typeof leadLocales)[number];

export type LeadForm = {
  restaurant: string;
  email: string;
  city: string;
  interest: LeadInterest;
};

export type LeadSubmission = LeadForm & {
  locale: LeadLocale;
  website: string;
};

export type LeadErrors = Partial<Record<"restaurant" | "email" | "city", string>>;

export type LeadFieldIssue = {
  path: "restaurant" | "email" | "city" | "interest" | "locale";
  code: "required" | "invalid_format" | "invalid_value" | "too_long";
};

export type ParsedLeadSubmission = {
  value?: LeadSubmission;
  fieldIssues: LeadFieldIssue[];
};

const restaurantMaxLength = 120;
const emailMaxLength = 254;
const cityMaxLength = 120;
const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function text(value: unknown): string {
  return typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
}

function email(value: unknown): string {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

export function hasFilledLeadHoneypot(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false;
  const website = (value as Record<string, unknown>).website;
  return typeof website === "string" && website.trim().length > 0;
}

export function parseLeadSubmission(value: unknown): ParsedLeadSubmission {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return {
      fieldIssues: [
        { path: "restaurant", code: "required" },
        { path: "email", code: "required" },
        { path: "city", code: "required" },
        { path: "interest", code: "invalid_value" },
        { path: "locale", code: "invalid_value" },
      ],
    };
  }

  const input = value as Record<string, unknown>;
  const restaurant = text(input.restaurant);
  const workEmail = email(input.email);
  const city = text(input.city);
  const interest = input.interest;
  const locale = input.locale;
  const fieldIssues: LeadFieldIssue[] = [];

  if (!restaurant) fieldIssues.push({ path: "restaurant", code: "required" });
  else if (restaurant.length > restaurantMaxLength) {
    fieldIssues.push({ path: "restaurant", code: "too_long" });
  }

  if (!workEmail) fieldIssues.push({ path: "email", code: "required" });
  else if (workEmail.length > emailMaxLength) {
    fieldIssues.push({ path: "email", code: "too_long" });
  } else if (!emailPattern.test(workEmail)) {
    fieldIssues.push({ path: "email", code: "invalid_format" });
  }

  if (!city) fieldIssues.push({ path: "city", code: "required" });
  else if (city.length > cityMaxLength) {
    fieldIssues.push({ path: "city", code: "too_long" });
  }

  if (!leadInterests.includes(interest as LeadInterest)) {
    fieldIssues.push({ path: "interest", code: "invalid_value" });
  }
  if (!leadLocales.includes(locale as LeadLocale)) {
    fieldIssues.push({ path: "locale", code: "invalid_value" });
  }

  if (fieldIssues.length > 0) return { fieldIssues };
  return {
    fieldIssues,
    value: {
      restaurant,
      email: workEmail,
      city,
      interest: interest as LeadInterest,
      locale: locale as LeadLocale,
      website: typeof input.website === "string" ? input.website : "",
    },
  };
}

export function validateLeadForm(values: LeadForm): LeadErrors {
  const { fieldIssues } = parseLeadSubmission({
    ...values,
    locale: "en",
    website: "",
  });
  const errors: LeadErrors = {};
  for (const issue of fieldIssues) {
    if (issue.path === "restaurant") {
      errors.restaurant =
        issue.code === "too_long"
          ? "official.contact.restaurantTooLong"
          : "official.contact.required";
    }
    if (issue.path === "email") {
      errors.email =
        issue.code === "required"
          ? "official.contact.emailRequired"
          : issue.code === "too_long"
            ? "official.contact.emailTooLong"
            : "official.contact.emailInvalid";
    }
    if (issue.path === "city") {
      errors.city =
        issue.code === "too_long"
          ? "official.contact.cityTooLong"
          : "official.contact.cityRequired";
    }
  }
  return errors;
}
